/**
 * Positional rows to keyed objects (SPEC §8.6).
 *
 * Response rows are arrays, in `columns` order, with no keys — that is what keeps
 * a wide result set small and the generic table renderer trivial. The
 * transplanted report components were written against keyed objects, so this is
 * the one adaptation the port needs, in one place rather than at every read site.
 *
 * The mapping is driven by the response's own `columns`, never by a hard-coded
 * index. An index would silently read the wrong column the moment a manifest
 * gains one, which is precisely the failure the server-side drift check exists to
 * prevent — reproducing it in the client would be a poor trade.
 *
 * A column the caller asks for that the dataset does not have is `undefined`
 * rather than a thrown error, and the caller's type says so: the manifest and
 * this file are checked against each other at runtime by the drift check, so a
 * mismatch here means a manifest edit, not corrupt data.
 */

import type { Cell, DatasetResult, ReportResult } from '../../lib/types'

/** One row, keyed by column. */
export type Keyed = Readonly<Record<string, Cell>>

/** Every row of a dataset, keyed by its own `columns`. */
export function keyRows(dataset: DatasetResult | undefined): Keyed[] {
  if (dataset === undefined) return []
  const keys = dataset.columns.map((column) => column.key)
  return dataset.rows.map((row) => {
    const keyed: Record<string, Cell> = {}
    keys.forEach((key, index) => {
      keyed[key] = row[index] ?? null
    })
    return keyed
  })
}

/**
 * A named dataset from a run payload, keyed.
 *
 * `primary` is the response's top-level `columns`/`rows` (§8.6), so it is read
 * from there rather than from `datasets` — where it deliberately does not appear.
 */
export function dataset(result: ReportResult, key: string): Keyed[] {
  if (key === 'primary') {
    return keyRows({
      columns: result.columns,
      rows: result.rows,
      row_count: result.row_count,
      truncated: result.truncated,
    })
  }
  return keyRows(result.datasets[key])
}

/**
 * A cell as a string, or `null`. Blank strings collapse to `null`.
 *
 * Takes `Cell | undefined` because `noUncheckedIndexedAccess` makes every lookup
 * on a `Keyed` row optional — a column the manifest does not declare is
 * `undefined` here rather than a crash, and it renders exactly as a `NULL` does.
 */
export function text(value: Cell | undefined): string | null {
  if (value === null || value === undefined) return null
  const asString = String(value)
  return asString === '' ? null : asString
}

/** A cell as a number, or `0` — for counts, which are never legitimately null. */
export function count(value: Cell | undefined): number {
  return typeof value === 'number' ? value : Number(value ?? 0)
}
